import React from "react";
//#region types
import { RecurringPeriodisationProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { DateConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { MessageBubble, ToggleSwitch } from "components/common";
import { Grid } from "@mui/material";
import { NumberInput } from "components/common/wintForms";
import { useRecurringInvoiceService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const RecurringPeriodisation: React.FC<RecurringPeriodisationProps> = (props) => {
  const recurringInvoiceService = useRecurringInvoiceService();
  const {
    name,
    value,
    error,
    touched,
    setValue,
    onBlur,
    disabled,
    recurringInvoiceStartDate,
    recurringInvoiceEndDate,
    recurringInvoiceFrequency,
    isPeriodisationAdvancedConfiguration,
    stacked,
    onInputFocus,
  } = props;
  const inputColumns = stacked ? 12 : 6;

  const onFromChange = (e: React.ChangeEvent<any>) => {
    setValue({ ...value, from: e.target.value });
  };

  const onDurationChange = (e: React.ChangeEvent<any>) => {
    setValue({ ...value, duration: e.target.value });
  };

  const onToggleChange = (e: React.ChangeEvent<HTMLInputElement>, isEnabled: boolean) => {
    e.stopPropagation();
    setValue({ ...value, isEnabled: isEnabled });
  };

  const getHelpText = () => {
    if (
      !touched ||
      error ||
      recurringInvoiceStartDate === undefined ||
      isPeriodisationAdvancedConfiguration ||
      recurringInvoiceFrequency === undefined ||
      value?.from === undefined ||
      value?.duration === undefined
    ) {
      return undefined;
    } else {
      const recurringStart = recurringInvoiceStartDate;
      const recurringEnd = recurringInvoiceEndDate;

      const firstPeriodisation = DateHelper.addMonths(recurringStart, value.from);
      const firstPeriodisationEnd = DateHelper.addMonths(firstPeriodisation, value.duration - 1);

      const secondInvoice = DateHelper.addMonths(
        recurringStart,
        recurringInvoiceService.getFrequencyMonths(recurringInvoiceFrequency)
      );
      const secondPeriodisation = DateHelper.addMonths(secondInvoice, value.from);
      const secondPeriodisationEnd = DateHelper.addMonths(secondPeriodisation, value.duration - 1);

      const showYear =
        DateHelper.getYear(firstPeriodisation) !== DateHelper.getYear(firstPeriodisationEnd) ||
        DateHelper.getYear(secondPeriodisation) !== DateHelper.getYear(secondPeriodisationEnd);
      const dateConfig = showYear ? DateConstants.MonthYearLongConfig : DateConstants.MonthLongConfig;

      const firstPeriodisationMonth = LocalizationHelperInstance.getFormattedDate(firstPeriodisation, dateConfig);
      const firstPeriodisationEndMonth = LocalizationHelperInstance.getFormattedDate(firstPeriodisationEnd, dateConfig);

      const recurringStartMonth = LocalizationHelperInstance.getFormattedDate(recurringStart, dateConfig);

      const updatedHelpText =
        firstPeriodisationEndMonth === firstPeriodisationMonth && firstPeriodisationMonth === recurringStartMonth
          ? undefined
          : `${LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.RecurringFirstPeriodisationInfo,
              undefined,
              {
                firstPeriodisationStartMonth: firstPeriodisationMonth,
                firstPeriodisationEndMonth: firstPeriodisationEndMonth,
                length: value.duration.toString(),
              }
            )}${
              recurringEnd === undefined || DateHelper.isBefore(secondInvoice, recurringEnd)
                ? LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Invoice.RecurringSecondPeriodisationInfo,
                    undefined,
                    {
                      secondPeriodisationStartMonth: LocalizationHelperInstance.getFormattedDate(
                        secondPeriodisation,
                        dateConfig
                      ),
                      secondPeriodisationEndMonth: LocalizationHelperInstance.getFormattedDate(
                        secondPeriodisationEnd,
                        dateConfig
                      ),
                      length: value.duration.toString(),
                    }
                  )
                : ""
            }.`;

      return updatedHelpText;
    }
  };

  return (
    <Grid
      item
      container
      xs={stacked ? 12 : undefined}
      spacing={1}
      alignItems="center"
      className={clsx(cssExports.recurringPeriodisationContainer, stacked && cssExports.stackedPeriodisation)}
    >
      <Grid item xs={stacked ? 12 : true} container spacing={1} onFocus={onInputFocus}>
        <Grid item xs={inputColumns}>
          <NumberInput
            name={`${name}.duration`}
            label={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.RecurringPeriodisationDuration
            )}
            value={value?.duration}
            error={error?.duration}
            touched={touched?.duration}
            allowNegative={false}
            onChange={onDurationChange}
            suffix={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MonthOrMonths)}
            onBlur={onBlur}
            setValue={() => {}}
            setTouched={() => {}}
            hasError={error?.duration && touched?.duration}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={inputColumns}>
          <MessageBubble message={getHelpText()} type={StyleEnums.DisplayType.Info} placement="right">
            <NumberInput
              name={`${name}.from`}
              label={LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Invoice.RecurringPeriodisationStart
              )}
              value={value?.from}
              error={error?.from}
              touched={touched?.from}
              allowNegative
              onChange={onFromChange}
              suffix={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MonthOrMonths)}
              onBlur={onBlur}
              setValue={() => {}}
              setTouched={() => {}}
              hasError={error?.from && touched?.from}
              disabled={disabled}
            />
          </MessageBubble>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container direction="row" spacing={1}>
          <Grid item className={cssExports.periodisationToggleLabel}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.PeriodisationAccounting)}
          </Grid>
          <Grid item>
            <ToggleSwitch checked={!!value?.isEnabled} onChange={onToggleChange} disabled={disabled} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RecurringPeriodisation;
